<script setup lang="ts" generic="T extends NutsProductVariant | NutsProductProjectionVariant">
import { money } from '@nuts/auto-delivery-sdk/dist/utils/format';
import { cents } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { computed } from 'vue';

import Caption from '@/components/base/typography/Caption.vue';
import Header6 from '@/components/base/typography/Header6.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import { getMoneyValue } from '@/lib/product/prices';
import { computeSavings, Money } from '@/utils/money';
import { getPrice, getUnitName, NutsProductVariant, PurchaseOptions } from '@/utils/product';
import { NutsProductProjectionVariant } from '@/utils/productProjection';

const {
  isGiftCertificate,
  isPrimary,
  isSelected,
  selections,
  smallestVariant,
  showSavings,
  variant,
} = defineProps<{
  isGiftCertificate?: boolean;
  isPrimary: boolean;
  isSelected: boolean;
  showSavings: boolean;
  selections?: PurchaseOptions;
  smallestVariant?: T | null;
  variant: T;
}>();

const selection = defineModel<string | number>();

const unitName = computed(() => {
  if ('unitName' in variant) {
    return variant.unitName;
  }

  return getUnitName(variant.variantName);
});

const priceBySku = computed(() => {
  if (!('otherVariations' in variant)) {
    return new Map<string | number, NutsProductVariant['prices'][number]>();
  }

  const variants = variant.otherVariations?.flatMap(({ variations }) => variations) ?? [];
  return new Map(
    variants.map((v) => [
      isGiftCertificate ? v.id : v.sku,
      getPrice(v, selections?.autoDelivery?.key),
    ]),
  );
});
const selectedPrice = computed(
  () =>
    priceBySku.value.get(selection.value || '') ?? getPrice(variant, selections?.autoDelivery?.key),
);

const savingsDetails = computed(() => {
  if (!showSavings) {
    return undefined;
  }

  if (!smallestVariant || !smallestVariant.weight || !variant.weight) {
    return undefined;
  }

  const currentPriceObj = getMoneyValue(selectedPrice.value);
  const smallestPriceObj = getPrice(smallestVariant, selections?.autoDelivery?.key);

  if (!currentPriceObj || !smallestPriceObj) {
    return undefined;
  }

  const currentPricePerWeight = Money.divide(currentPriceObj, variant.weight);
  const smallestPricePerWeight = Money.divide(smallestPriceObj.value, smallestVariant.weight);

  if (cents(currentPricePerWeight) >= cents(smallestPricePerWeight)) {
    return undefined;
  }

  const savings = computeSavings(smallestPricePerWeight, currentPricePerWeight);

  return {
    percentage: savings.percent,
  };
});

const isSingleServeOrSample = computed(
  () => unitName.value?.toLowerCase().includes('single serve') || variant.sample,
);
</script>

<template>
  <div
    class="flex relative justify-center items-center px-2 py-1.5 h-16 rounded-md border md:h-20"
    :class="[
      isSelected
        ? 'bg-nuts-neutral-100 border-black'
        : 'bg-white border-nuts-neutral-200 hover:bg-zinc-50',
      variant.backordered ? 'border-dashed' : 'border-solid',
    ]"
  >
    <Caption
      v-if="isPrimary"
      class="absolute top-0 right-0 left-0 py-0.5 font-semibold text-center text-white bg-black rounded-t-md"
    >
      Most Popular
    </Caption>
    <label class="flex w-full h-full cursor-pointer" data-test="variant-option">
      <input
        v-model="selection"
        :aria-label="`Select ${unitName}`"
        class="absolute top-0 right-0 bottom-0 left-0 p-0 m-0 w-full h-full rounded-md appearance-none cursor-pointer"
        :id="`variant-${isGiftCertificate ? variant.id : variant.sku}`"
        name="variant"
        type="radio"
        :value="isGiftCertificate ? variant.id : variant.sku"
      />
      <Header6 v-if="isGiftCertificate" class="font-semibold font-sofia-pro">
        {{ money(variant.prices[0].value) }}
      </Header6>
      <div
        v-else-if="unitName"
        class="flex flex-col justify-center items-start w-full h-full"
        :class="[isPrimary ? 'pt-4' : 'pt-2']"
      >
        <div class="flex flex-row justify-between items-start w-full">
          <Header6 class="font-sofia-pro" :class="{ title: !isSelected }">
            {{ `${unitName[0].toUpperCase()}${unitName.slice(1)}` }}
          </Header6>
          <Caption
            v-if="savingsDetails?.percentage && !isSingleServeOrSample"
            class="px-1 py-0.5 font-semibold text-black whitespace-nowrap rounded-2xl bg-nuts-amber-400"
          >
            Save {{ savingsDetails.percentage }}%
          </Caption>
        </div>
        <div class="flex gap-1 items-center">
          <SmallBodyText
            class="font-normal"
            :class="[
              isSelected ? 'text-black' : 'text-neutral-500',
              { 'text-nuts-red-800': selectedPrice.discounted },
            ]"
          >
            {{ money(getMoneyValue(selectedPrice)) }}
          </SmallBodyText>
        </div>
      </div>
    </label>
  </div>
</template>

<style scoped>
label {
  @apply m-0;
}

.title {
  @apply font-normal;
}
</style>
